import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Save, Eye } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { useTenant } from "@/contexts/TenantContext";
import { useToast } from "@/hooks/use-toast";
import { usePageTitle } from "@/contexts/PageTitleContext";
import { getBreadcrumbBackPath } from "@/lib/breadcrumbs";
import { buildPathWithCurrentTenant } from "@/lib/tenantPath";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

import SectionCard from "@/pages/appointmentDetails/SectionCard";
import { fmtDateTime, formatDobValue, statusPill } from "@/pages/appointmentDetails/helpers";
import HouseholdDetailsCard from "@/components/HouseholdDetailsCard";
import { DetailPageSkeleton } from "@/components/skeletons/DetailPageSkeleton";

// Button styling classes to match Appointment Details patterns
const accentIconButtonClass =
  "h-9 w-9 rounded-full bg-indigo-500/20 p-0 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/30";
const saveIconButtonClass =
  "h-9 w-9 rounded-full bg-amber-500/20 p-0 text-amber-100 ring-1 ring-amber-400/30 hover:bg-amber-500/30";

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.filter(Boolean).join(", ") || "—";
  return String(value);
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(String(value).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};

const formatDate = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}/${m[3]}/${m[1]}`;
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = String(d.getFullYear());
    return `${mm}/${dd}/${yyyy}`;
  }
  return "—";
};

const formatDiscoveryValue = (field, values) => {
  const normalizeValue = (value) => {
    if (value === null || value === undefined) return "";
    if (Array.isArray(value)) {
      return value.map(normalizeValue).filter(Boolean).join(", ");
    }
    if (typeof value === "object") {
      if (value.value !== undefined) return normalizeValue(value.value);
      if (value.label) return String(value.label);
      if (value.name) return String(value.name);
      return "";
    }
    return String(value);
  };

  const clean = (values || []).map((v) => normalizeValue(v).trim()).filter(Boolean);
  if (clean.length === 0) return "—";
  if (field?.field_type === "checkbox") {
    return clean.map((v) => (String(v).toLowerCase() === "true" ? "Yes" : "No")).join(", ");
  }
  return clean.join(", ");
};


export default function ClientDetailsPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { selectedTenantId } = useTenant();

  // Function to invalidate clients list cache after mutations
  const invalidateClientsCache = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.clients.all(selectedTenantId),
    });
  }, [queryClient, selectedTenantId]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [client, setClient] = useState(null);
  const [lead, setLead] = useState(null);
  const [appointment, setAppointment] = useState(null);
  const [discovery, setDiscovery] = useState({ fields: [], values: [] });
  const [firstAppointmentCreatedAt, setFirstAppointmentCreatedAt] = useState(null);
  const [householdData, setHouseholdData] = useState(null);
  
  // Edit mode state for Client Details card
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const pageTitle = client?.full_name || lead?.full_name || "Client Details";
  usePageTitle(pageTitle);

  useEffect(() => {
    const fetchClient = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/clients/${clientId}`);
        setClient(res.data?.client || null);
        setLead(res.data?.lead || null);
        setAppointment(res.data?.latest_appointment || null);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load client details.");
        toast({ title: "Could not load client", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };
    if (clientId) fetchClient();
  }, [clientId, toast]);

  const soldPayloads = useMemo(() => {
    if (Array.isArray(appointment?.sold_payloads) && appointment.sold_payloads.length) return appointment.sold_payloads;
    if (appointment?.sold_payload) return [appointment.sold_payload];
    if (client?.sold_payload) return [client.sold_payload];
    return [];
  }, [appointment, client]);

  const productRecords = useMemo(() => {
    if (client?.sold_payloads?.length) return client.sold_payloads;
    if (client?.sold_payload) return [client.sold_payload];
    return soldPayloads;
  }, [client, soldPayloads]);

  const appointmentId = appointment?.id || client?.appointment_id || null;

  useEffect(() => {
    if (!appointmentId) return;
    const loadDiscovery = async () => {
      try {
        const res = await api.get(`/appointments/${appointmentId}/discovery`);
        setDiscovery(res.data || { fields: [], values: [] });
      } catch {
        setDiscovery({ fields: [], values: [] });
      }
    };
    loadDiscovery();
  }, [appointmentId]);

  useEffect(() => {
    const leadId = lead?.id || client?.primary_lead_id;
    if (!leadId) {
      setFirstAppointmentCreatedAt(null);
      return;
    }
    const loadFirstAppointment = async () => {
      try {
        const res = await api.get("/appointments", {
          params: {
            filters: JSON.stringify([{ field: "lead_id", operator: "equals", value: leadId }]),
            sort_by: "created_at",
            sort_dir: "asc",
            limit: 1,
            offset: 0,
          },
        });
        const first = res.data?.items?.[0];
        setFirstAppointmentCreatedAt(first?.created_at || null);
      } catch {
        setFirstAppointmentCreatedAt(null);
      }
    };
    loadFirstAppointment();
  }, [lead?.id, client?.primary_lead_id]);

  // Fetch household members when lead is available
  useEffect(() => {
    const leadId = lead?.id;
    if (!leadId) {
      setHouseholdData(null);
      return;
    }
    const loadHousehold = async () => {
      try {
        const res = await api.get(`/leads/${leadId}/household-members`);
        setHouseholdData(res.data || null);
      } catch {
        setHouseholdData(null);
      }
    };
    loadHousehold();
  }, [lead?.id]);

  // Initialize edit form when entering edit mode
  useEffect(() => {
    if (editMode && lead) {
      setEditForm({
        dob: lead?.dob || "",
        phone_number: lead?.phone_number || client?.phone_number || "",
        email: lead?.email || client?.email || "",
        state: lead?.state || client?.state || "",
        full_name: lead?.full_name || client?.full_name || "",
      });
    }
  }, [editMode, lead, client]);

  // Cancel edit mode when navigating away or on load
  useEffect(() => {
    setEditMode(false);
    setEditForm({});
  }, [clientId]);

  const handleSaveClientDetails = async () => {
    if (!lead?.id) {
      toast({ title: "Cannot save", description: "Lead information not available", variant: "destructive" });
      return;
    }
    
    setSaving(true);
    try {
      const payload = { ...editForm };
      // Convert empty strings to null for proper backend handling
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "") payload[key] = null;
      });
      
      const res = await api.put(`/leads/${lead.id}`, payload);
      setLead(res.data);
      toast({ title: "Client details saved" });
      setEditMode(false);
      // REACT QUERY: Invalidate clients list cache after updating client details
      invalidateClientsCache();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "Please try again.";
      toast({ title: "Could not save", description: message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleEditFormChange = (key, value) => {
    setEditForm((prev) => ({ ...prev, [key]: value }));
  };

  const clientDetailFields = useMemo(
    () => {
      const baseFields = [
        { key: "dob", label: "DOB", value: formatDobValue(lead?.dob) },
        { key: "phone", label: "Phone", value: lead?.phone_number || client?.phone_number },
        { key: "email", label: "Email", value: lead?.email || client?.email },
        { key: "state", label: "State", value: lead?.state || client?.state },
      ];

      const fields = Array.isArray(discovery?.fields) ? discovery.fields : [];
      const householdFields = fields.filter((f) => f?.scope !== "prospects");
      const householdValuesByKey = new Map();
      (discovery?.values || []).forEach((v) => {
        const fid = v?.field_id || v?.fieldId || v?.id || v?.key;
        if (!fid) return;
        const subjectId = v?.subject_id || "household";
        const key = `${fid}::${subjectId}`;
        if (!householdValuesByKey.has(key)) householdValuesByKey.set(key, []);
        householdValuesByKey.get(key).push(v);
      });

      const householdFieldsMapped = householdFields.map((field) => {
        const fid = field?.id || field?.field_id || field?.key;
        const key = `${fid}::household`;
        const values = householdValuesByKey.get(key) || [];
        return {
          key: `household-${fid}`,
          label: field?.label || field?.name || fid || "Field",
          value: formatDiscoveryValue(field, values),
        };
      });

      return [...baseFields, ...householdFieldsMapped];
    },
    [lead, client, discovery]
  );

  const leadDetailFields = useMemo(
    () => [
      { key: "lead_source", label: "Lead Source", value: lead?.lead_source_name || lead?.lead_source },
      { key: "campaign_name", label: "Campaign Name", value: lead?.campaign_name },
      { key: "ad_set_name", label: "Ad Set Name", value: lead?.ad_set_name },
      { key: "ad_name", label: "Ad Name", value: lead?.ad_name },
      { key: "form_name", label: "Form Name", value: lead?.form_name },
      { key: "lead_create_date", label: "Lead Create Date", value: formatDate(lead?.lead_create_date) },
      { key: "client_create_date", label: "Client Create Date", value: formatDate(client?.created_at) },
      { key: "appointment_create_date", label: "Appointment Create Date", value: formatDate(firstAppointmentCreatedAt) },
      { key: "ghl_contact_id", label: "GHL Contact ID", value: lead?.ghl_contact_id || lead?.lead_external_id },
      { key: "missionassist_id", label: "MissionAssist ID", value: lead?.id },
      { key: "household_id", label: "Household ID", value: lead?.household_id },
    ],
    [lead, client, firstAppointmentCreatedAt]
  );

  const appointmentRow = useMemo(() => {
    if (!appointment) return null;
    return {
      status: appointment?.status || "—",
      agent: appointment?.sales_agent_name || appointment?.booking_agent_name || "—",
      time: fmtDateTime(appointment?.start_at),
      timezone: appointment?.timezone || "—",
    };
  }, [appointment]);


  return (
    <div className="space-y-6" data-testid="client-details-page">
      <div className="flex items-center justify-between gap-3" data-testid="client-details-toolbar">
        <Button
          type="button"
          variant="secondary"
          className="h-9 rounded-full bg-indigo-500/15 px-4 text-indigo-100 ring-1 ring-indigo-400/20 hover:bg-indigo-500/25"
          onClick={() => navigate(getBreadcrumbBackPath("/app/clients"))}
          data-testid="client-details-back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>


      {loading ? (
        <DetailPageSkeleton variant="default" />
      ) : error ? (
        <Card className="glass" data-testid="client-details-error">
          <CardContent className="p-6 text-sm text-rose-200">{error}</CardContent>
        </Card>
      ) : (
        <>
          <SectionCard 
            title="Client Details"
            right={
              editMode ? (
                <Button
                  type="button"
                  variant="secondary"
                  className={saveIconButtonClass}
                  onClick={handleSaveClientDetails}
                  disabled={saving}
                  data-testid="client-details-save-button"
                >
                  <Save className="h-4 w-4" />
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  className={accentIconButtonClass}
                  onClick={() => setEditMode(true)}
                  data-testid="client-details-edit-button"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              )
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="client-details-section">
              {editMode ? (
                <>
                  <div className="space-y-1" data-testid="client-details-field-dob-edit">
                    <div className="text-xs text-slate-200/60">DOB</div>
                    <Input
                      type="date"
                      value={editForm.dob || ""}
                      onChange={(e) => handleEditFormChange("dob", e.target.value)}
                      className="bg-white/5 ring-1 ring-white/10 text-sm text-slate-100"
                      data-testid="client-details-input-dob"
                    />
                  </div>
                  <div className="space-y-1" data-testid="client-details-field-phone-edit">
                    <div className="text-xs text-slate-200/60">Phone</div>
                    <Input
                      type="tel"
                      value={editForm.phone_number || ""}
                      onChange={(e) => handleEditFormChange("phone_number", e.target.value)}
                      className="bg-white/5 ring-1 ring-white/10 text-sm text-slate-100"
                      data-testid="client-details-input-phone"
                    />
                  </div>
                  <div className="space-y-1" data-testid="client-details-field-email-edit">
                    <div className="text-xs text-slate-200/60">Email</div>
                    <Input
                      type="email"
                      value={editForm.email || ""}
                      onChange={(e) => handleEditFormChange("email", e.target.value)}
                      className="bg-white/5 ring-1 ring-white/10 text-sm text-slate-100"
                      data-testid="client-details-input-email"
                    />
                  </div>
                  <div className="space-y-1" data-testid="client-details-field-state-edit">
                    <div className="text-xs text-slate-200/60">State</div>
                    <Input
                      value={editForm.state || ""}
                      onChange={(e) => handleEditFormChange("state", e.target.value)}
                      className="bg-white/5 ring-1 ring-white/10 text-sm text-slate-100"
                      data-testid="client-details-input-state"
                    />
                  </div>
                </>
              ) : (
                clientDetailFields.map((field) => (
                  <div key={field.key} className="space-y-1" data-testid={`client-details-field-${field.key}`}>
                    <div className="text-xs text-slate-200/60" data-testid={`client-details-label-${field.key}`}>{field.label}</div>
                    <div className="text-sm text-slate-100" data-testid={`client-details-value-${field.key}`}>
                      {formatValue(field.value)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </SectionCard>

          <SectionCard title="Products">
            <div className="space-y-3" data-testid="client-products-section">
              <div
                className="grid grid-cols-1 gap-2 px-2 text-xs text-slate-200/60 sm:grid-cols-[140px_1fr_1fr_160px]"
                data-testid="client-products-header"
              >
                <div>Product Status</div>
                <div>Carrier</div>
                <div>Product Name</div>
                <div>Agent Commission</div>
              </div>

              {productRecords.length === 0 ? (
                <div
                  className="rounded-xl bg-white/5 px-3 py-4 text-sm text-slate-200/70"
                  data-testid="client-products-empty"
                >
                  No products found.
                </div>
              ) : (
                productRecords.map((payload, idx) => (
                  <div
                    key={`product-${idx}`}
                    className="rounded-xl bg-white/5 px-3 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(buildPathWithCurrentTenant(`/app/clients/${clientId}/products/${idx}`))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(buildPathWithCurrentTenant(`/app/clients/${clientId}/products/${idx}`));
                      }
                    }}
                    data-testid={`client-products-row-${idx}`}
                  >
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_1fr_1fr_160px]">
                      <div data-testid={`client-products-status-${idx}`}>
                        <Badge className={`ring-1 ${statusPill(payload?.product_status || "Pending")}`}>
                          {payload?.product_status || "Pending"}
                        </Badge>
                      </div>
                      <div className="text-sm text-slate-100" data-testid={`client-products-carrier-${idx}`}>
                        {formatValue(payload?.company)}
                      </div>
                      <div className="text-sm text-slate-100" data-testid={`client-products-name-${idx}`}>
                        {formatValue(payload?.product_name)}
                      </div>
                      <div className="text-sm text-slate-100" data-testid={`client-products-commission-${idx}`}>
                        {payload?.agent_commission ? formatCurrency(payload.agent_commission) : "—"}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </SectionCard>

          <SectionCard title="Latest Appointment">
            <div className="space-y-3" data-testid="client-latest-appointment-section">
              <div
                className="grid grid-cols-1 gap-2 px-2 text-xs text-slate-200/60 sm:grid-cols-[140px_1fr_1fr_160px]"
                data-testid="client-appointment-header"
              >
                <div>Status</div>
                <div>Agent</div>
                <div>Appointment Time</div>
                <div>Time Zone</div>
              </div>

              {!appointmentRow ? (
                <div
                  className="rounded-xl bg-white/5 px-3 py-4 text-sm text-slate-200/70"
                  data-testid="client-appointment-empty"
                >
                  No appointments found.
                </div>
              ) : (
                <div
                  className={`rounded-xl bg-white/5 px-3 py-3 text-left hover:bg-white/10 hover:ring-1 hover:ring-white/10 ${
                    appointmentId ? "cursor-pointer" : ""
                  }`}
                  role={appointmentId ? "button" : "group"}
                  tabIndex={appointmentId ? 0 : -1}
                  onClick={() => {
                    if (appointmentId) navigate(buildPathWithCurrentTenant(`/app/appointments/${appointmentId}`));
                  }}
                  onKeyDown={(e) => {
                    if (!appointmentId) return;
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(buildPathWithCurrentTenant(`/app/appointments/${appointmentId}`));
                    }
                  }}
                  data-testid="client-appointment-row"
                >
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_1fr_1fr_160px]">
                    <div data-testid="client-appointment-status">
                      <Badge className={`ring-1 ${statusPill(appointmentRow.status)}`}>{appointmentRow.status}</Badge>
                    </div>
                    <div className="text-sm text-slate-100" data-testid="client-appointment-agent">
                      {appointmentRow.agent}
                    </div>
                    <div className="text-sm text-slate-100" data-testid="client-appointment-time">
                      {appointmentRow.time}
                    </div>
                    <div className="text-sm text-slate-100" data-testid="client-appointment-timezone">
                      {appointmentRow.timezone}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </SectionCard>

          {/* Household Details Card - placed between Latest Appointment and Lead Details */}
          <HouseholdDetailsCard 
            householdData={householdData} 
            currentLeadId={lead?.id}
            linkPrefix="/app/leads"
          />

          <SectionCard 
            title="Lead Details"
            right={
              lead?.id && (
                <Button
                  type="button"
                  variant="secondary"
                  className={accentIconButtonClass}
                  onClick={() => navigate(buildPathWithCurrentTenant(`/app/leads/${lead.id}`))}
                  data-testid="client-lead-details-view-button"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              )
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="client-lead-details-section">
              {leadDetailFields.map((field) => (
                <div key={field.key} className="space-y-1" data-testid={`client-lead-field-${field.key}`}>
                  <div className="text-xs text-slate-200/60" data-testid={`client-lead-label-${field.key}`}>{field.label}</div>
                  <div className="text-sm text-slate-100" data-testid={`client-lead-value-${field.key}`}>
                    {formatValue(field.value)}
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>

        </>
      )}
    </div>
  );
}